import { Currency } from '@/modules/common/types/currency';
import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { RateSource } from '@/modules/common/types/rate-source';

import { Manager, ProjectBillingModel, ProjectStatus } from './project';
import { ProjectTeamMember } from './project-team-member';

/**
 * Change date range
 * Used in EditChange mode to track the original dates of a change
 */
export interface ChangeDateRange {
  startDate: string;
  endDate: string | null;
}

/**
 * Domain enum for patch effect actions
 */
export enum PatchEffectAction {
  Delete = 'delete',
  Cut = 'cut',
}

/**
 * Domain model for patch entity effect
 * Describes the impact of a patch operation on an existing entity change
 */
export interface PatchEntityEffect {
  /**
   * Action that will be taken on the existing change
   */
  action: PatchEffectAction;

  /**
   * New start date if the change is being cut
   * Only populated when action is Cut
   */
  cutEffectiveStartDate: string | null;

  /**
   * New end date if the change is being cut
   * Only populated when action is Cut
   */
  cutEffectiveEndDate: string | null;
}

/**
 * Domain model for project info versioned fields
 * Contains the fields that can change over time
 */
export interface ProjectInfoVersionedFields {
  status: ProjectStatus;
  tentativeProbability: number | null;
  managers: Manager[];
  projectStartDate: string | null;
  projectEndDate: string | null;
  industries: string[] | null;
  customAttributesValues: CustomAttribute[];
}

/**
 * Domain model for a single project info snapshot
 * Represents the state of project info at a specific time period
 */
export interface ProjectInfoSnapshot {
  /**
   * The actual project info data for this time period
   */
  snapshot: ProjectInfoVersionedFields;

  /**
   * Describes what will happen to this snapshot if the new change is applied
   */
  patchEffect: PatchEntityEffect;

  /**
   * When this snapshot becomes effective
   */
  effectiveStartDate: string;

  /**
   * When this snapshot ends (null means infinite/no end date)
   */
  effectiveEndDate: string | null;
}

/**
 * Domain model for project billing snapshot data
 * Contains the billing configuration at a specific time
 */
export interface ProjectBillingSnapshotData {
  billingModel: ProjectBillingModel | null;
  defaultCurrency: Currency;
  rateSource: RateSource;
  flatRate: number | null;
  budget: number | null;
  userRates: ProjectTeamMember[];
  hasAnyDeletedRelations: boolean;
}

/**
 * Domain model for a single project billing snapshot
 * Represents the state of project billing at a specific time period
 */
export interface ProjectBillingSnapshot {
  /**
   * The actual project billing data for this time period
   */
  snapshot: ProjectBillingSnapshotData;

  /**
   * Describes what will happen to this snapshot if the new change is applied
   */
  patchEffect: PatchEntityEffect;

  /**
   * When this snapshot becomes effective
   */
  effectiveStartDate: string;

  /**
   * When this snapshot ends (null means infinite/no end date)
   */
  effectiveEndDate: string | null;
}

/**
 * Generic conflict resolution data
 * Can contain either info or billing snapshots
 */
export interface ConflictResolutionData {
  items: ProjectInfoSnapshot[] | ProjectBillingSnapshot[];
}

/**
 * Sub-row data for array fields that generate nested table rows
 * Used for fields like team members where each array item becomes a sub-row
 * under the parent field row (e.g., "userRates" field expands into individual member sub-rows)
 */
export interface SubRowData {
  /**
   * Unique identifier for this sub-row item (e.g., team member ID)
   */
  id: string;

  /**
   * Label to display in the Attribute column
   */
  label: string;

  /**
   * Optional avatar URL for rendering
   */
  avatarUrl?: string;

  /**
   * Current value from the form for this item
   */
  currentValue: any;

  /**
   * Values from each snapshot, keyed by snapshot ID
   */
  snapshotValues: Map<string, any>;
}

/**
 * Field configuration for conflict resolution table
 * Defines how a field should be displayed and processed
 */
export interface ConflictFieldConfig<TFieldKey extends string = string> {
  /**
   * Unique identifier for the field (matches the form field key)
   */
  key: TFieldKey;

  /**
   * Optional key to use when reading from snapshot data
   * If not provided, uses `key` as the snapshot field name
   */
  snapshotKey?: string;

  /**
   * Translation key for the field label
   */
  labelKey: string;

  /**
   * If true, this field will never be shown in the conflict resolution table
   * Use for fields like name, status, colorHex, client, description, etc.
   */
  alwaysExcluded?: boolean;

  /**
   * Optional formatter to convert raw value to display string
   * Can optionally receive context (e.g., currency) for formatting
   */
  valueFormatter?: (value: any, context?: any) => string;

  /**
   * Optional parser to convert display string back to raw value
   */
  valueParser?: (value: string) => any;

  /**
   * Optional custom renderer for complex value types
   */
  renderValue?: (value: any) => React.ReactNode;

  /**
   * Optional function to extract comparable value from form data
   * Used when form value structure differs from snapshot value
   * e.g., form has managerId (string) but snapshot has manager (object with id)
   */
  getFormValueForComparison?: (formValue: any) => any;

  /**
   * Optional function to extract comparable value from snapshot data
   * Used when snapshot value structure differs from form value
   * e.g., snapshot has manager object but we need to compare with managerId
   */
  getSnapshotValueForComparison?: (snapshotValue: any) => any;

  /**
   * Optional transformer applied to snapshot values before display and comparison.
   * Use when the backend stores values on a different scale than the form.
   * Applied in createTableRow so both display and comparison receive the normalized value.
   *
   * @example
   * // Backend stores probability as 0-1, form uses 0-100
   * normalizeSnapshotValue: (value) => value != null ? Math.round(value * 100) : value
   */
  normalizeSnapshotValue?: (snapshotValue: any) => any;

  /**
   * If true, this field generates sub-rows (one per array item)
   * Used for array fields like userRates where each team member becomes a sub-row
   */
  hasSubRows?: boolean;

  /**
   * Function to generate sub-rows from array field
   * Required when hasSubRows is true
   * @param formValue - Current form value (array)
   * @param snapshots - All snapshot data
   * @returns Array of sub-row data
   */
  getSubRows?: (formValue: any, snapshots: ConflictSnapshot[]) => SubRowData[];

  /**
   * Optional custom renderer for the attribute column of sub-rows
   * Used to render avatars, icons, etc.
   */
  renderSubRowLabel?: (row: SubRowData) => React.ReactNode;

  /**
   * Optional transformer to convert the resolved value back to the form field format.
   * Used when currentFormValues are enriched (e.g., { id, name } objects) for display,
   * but the form field stores a simpler value (e.g., ID string).
   * Applied in getResolvedValues() before returning to the caller.
   *
   * @example
   * // managerId form field stores "42", but modal receives { id: 42, name: "John" }
   * resolvedValueToFormValue: (value) => value?.id?.toString() ?? null
   */
  resolvedValueToFormValue?: (value: any) => any;

  /**
   * Optional function to conditionally show/hide field based on selected values
   * @param selectedValues - Current selected values from the form
   * @returns true if field should be shown, false if hidden
   */
  shouldShow?: (selectedValues: Record<string, any>) => boolean;

  /**
   * Optional normalizer to convert snapshot values to the format expected by the form
   * Used when snapshot contains objects (e.g., { code: string }) but form expects primitives (e.g., string)
   * Ensures only the correct format is stored in form values during conflict resolution
   * @param value - Value from snapshot or form (may be object or primitive)
   * @returns Normalized value in the format expected by the form
   */
  normalizeFormValue?: (value: any) => any;

  /**
   * Optional function to conditionally disable the field's selector based on selected values
   * When disabled, the selector is replaced with a "-" placeholder and the resolved value is null
   * @param selectedValues - Current selected values from the form
   * @returns true if field should be disabled, false if enabled
   */
  isDisabled?: (selectedValues: Record<string, any>) => boolean;

  /**
   * If true, null/undefined values will not appear as options in the dropdown
   * Useful for fields where null is not a valid user selection (e.g., probability when TENTATIVE)
   */
  excludeNullOption?: boolean;

  /**
   * When set, this field is always included in the table whenever the named field is included —
   * even if this field itself has no differences between form and snapshot values.
   * Use when two fields are tightly coupled in the UI (e.g. rateSource + flatRate).
   */
  showAlongsideField?: TFieldKey;
}

/**
 * Field group configuration for sub-headers in conflict table
 * Used for grouping related fields under a category header
 */
export interface ConflictFieldGroup<TFieldKey extends string = string> {
  /**
   * Translation key for the group header
   */
  headerLabelKey: string;

  /**
   * Fields that belong to this group
   */
  fields: TFieldKey[];
}

/**
 * Row data for conflict resolution table
 * Represents a single field with all its values across snapshots
 */
export interface ConflictFieldRow {
  /**
   * Internal field identifier
   */
  fieldKey: string;

  /**
   * Localized display name for the field
   */
  fieldLabel: string;

  /**
   * Current value from the form
   */
  currentValue: any;

  /**
   * Values from each snapshot, keyed by snapshot ID
   */
  snapshotValues: Map<string, any>;

  /**
   * Currently selected value for this field
   */
  selectedValue: any;

  /**
   * Optional field type for rendering hints
   */
  type?: 'text' | 'select' | 'number' | 'date' | 'currency';

  /**
   * If true, this row is a sub-row from an array field
   */
  isSubRow?: boolean;

  /**
   * For sub-rows, the parent field key
   */
  parentFieldKey?: string;

  /**
   * For sub-rows, additional data like avatar URL
   */
  subRowData?: SubRowData;

  /**
   * Reference to the field config for rendering
   */
  fieldConfig?: ConflictFieldConfig<any>;
}

/**
 * Snapshot data structure for conflict modal
 * Generic format used by the modal component
 */
export interface ConflictSnapshot {
  /**
   * Unique identifier for this snapshot
   */
  id: string;

  /**
   * When this snapshot becomes effective
   */
  effectiveStartDate: string;

  /**
   * When this snapshot ends (null means infinite)
   */
  effectiveEndDate: string | null;

  /**
   * Patch effect describing what will happen to this snapshot
   */
  patchEffect: {
    /**
     * Action type: 'cut', 'delete', or other
     */
    action: PatchEffectAction | string;

    /**
     * New start date when action is 'cut'
     */
    cutEffectiveStartDate?: string | null;

    /**
     * New end date when action is 'cut' (null means infinite)
     */
    cutEffectiveEndDate?: string | null;
  };

  /**
   * Field data for this snapshot
   */
  data: Record<string, any>;
}
